<template>
  <div class="freight-table-carga">
    <div
      v-for="(row, index) in rows"
      :key="row.key"
      class="freight-table-carga__row"
    >
      <div class="freight-table-carga__field freight-table-carga__field--tipo-carga">
        <label class="freight-table-carga__label">
          <span class="freight-table-carga__required">*</span>
          Tipo carga
        </label>
        <a-select
          :value="row.tipoCarga || undefined"
          allow-clear
          placeholder="Selecione o tipo de carga"
          @change="(value) => onTipoCargaChange(index, value)"
        >
          <a-select-option
            v-for="option in tipoCargaOptions"
            :key="option.value"
            :value="option.value"
          >
            {{ option.label }}
          </a-select-option>
        </a-select>
      </div>

      <div class="freight-table-carga__field freight-table-carga__field--tipo">
        <label class="freight-table-carga__label">Tipo</label>
        <a-select
          :value="row.tipo || undefined"
          allow-clear
          placeholder="Selecione o tipo"
          @change="(value) => onTipoChange(index, value)"
        >
          <a-select-option
            v-for="option in tipoOptions"
            :key="option.value"
            :value="option.value"
            :disabled="isTipoDisabled(index, option.value)"
          >
            {{ option.label }}
          </a-select-option>
        </a-select>
      </div>

      <div class="freight-table-carga__actions">
        <a-switch
          :checked="isStatusAtivo(row.status)"
          @change="(checked) => onStatusChange(index, checked)"
        />

        <a-button
          v-if="index === rows.length - 1"
          class="freight-table-carga__add"
          :disabled="!canAddRow"
          aria-label="Adicionar tipo de carga"
          @click="addRow"
        >
          <a-icon type="plus" />
        </a-button>

        <a-button
          v-if="rows.length > 1"
          type="link"
          class="freight-table-carga__trash"
          aria-label="Remover tipo de carga"
          @click="removeRow(index)"
        >
          <a-icon type="delete" />
        </a-button>
      </div>
    </div>
  </div>
</template>

<script>
let rowSeq = 0;

function nextKey() {
  rowSeq += 1;
  return `carga-${rowSeq}`;
}

function sameValue(a, b) {
  if (a == null || b == null) return false;
  return String(a) === String(b);
}

function valuesEqual(a, b) {
  if (a == null && b == null) return true;
  if (a == null || b == null) return false;
  return String(a) === String(b);
}

// status_carga da API: descr "Ativo"/"Inativo" → valor (ex.: 1 / 2).
function resolveStatusValues(options) {
  const list = options || [];
  const ativo = list.find(
    (option) => String(option.label || "").toLowerCase() === "ativo"
  );
  const inativo =
    ativo != null
      ? list.find((option) =>
          String(option.label || "")
            .toLowerCase()
            .includes("inativo")
        )
      : null;

  return {
    ativo: ativo != null ? ativo.value : 1,
    inativo: inativo != null ? inativo.value : 2,
  };
}

function createRow(defaults = {}, statusAtivo = 1) {
  return {
    key: nextKey(),
    tipoCarga: defaults.tipoCarga != null ? defaults.tipoCarga : null,
    tipo: defaults.tipo != null ? defaults.tipo : null,
    status: defaults.status != null ? defaults.status : statusAtivo,
  };
}

function toPayload(row) {
  return {
    tipoCarga: row.tipoCarga,
    tipo: row.tipo,
    status: row.status,
  };
}

function isRowComplete(row) {
  return (
    row != null &&
    row.tipoCarga != null &&
    row.tipo != null &&
    row.status != null
  );
}

function resolveDefaultTipo(options) {
  const list = options || [];
  const normal = list.find(
    (option) => String(option.label || "").toLowerCase() === "normal"
  );
  if (normal) return normal.value;
  return list.length ? list[0].value : null;
}

export default {
  name: "FreightTableCarga",
  props: {
    // Array de { tipoCarga, tipo, status } (.sync com o formulário pai).
    value: {
      type: Array,
      default: () => [],
    },
    tipoCargaOptions: {
      type: Array,
      default: () => [],
    },
    tipoOptions: {
      type: Array,
      default: () => [],
    },
    statusOptions: {
      type: Array,
      default: () => [],
    },
  },
  data() {
    return {
      rows: [createRow({}, 1)],
    };
  },
  computed: {
    canAddRow() {
      const maxPairs =
        (this.tipoCargaOptions || []).length * (this.tipoOptions || []).length;
      if (!(maxPairs > 0 && this.rows.length < maxPairs)) return false;
      // Só permite adicionar outra carga quando as atuais estão completas.
      return this.rows.every(isRowComplete);
    },
    defaultTipo() {
      return resolveDefaultTipo(this.tipoOptions);
    },
    statusValues() {
      return resolveStatusValues(this.statusOptions);
    },
  },
  watch: {
    value: {
      immediate: true,
      handler(list) {
        if (!Array.isArray(list) || !list.length) return;
        if (this.isSamePayload(list)) return;
        const statusAtivo = this.statusValues.ativo;
        this.rows = list.map((item) =>
          createRow(
            {
              tipoCarga: item.tipoCarga,
              tipo: item.tipo != null ? item.tipo : this.defaultTipo,
              status: item.status != null ? item.status : statusAtivo,
            },
            statusAtivo
          )
        );
      },
    },
    tipoOptions: {
      immediate: true,
      handler() {
        this.applyDefaultTipo();
      },
    },
    statusOptions: {
      immediate: true,
      handler() {
        this.applyDefaultStatus();
      },
    },
  },
  created() {
    if (!Array.isArray(this.value) || !this.value.length) {
      this.emitChange();
    }
  },
  methods: {
    isSamePayload(list) {
      const current = this.rows.map(toPayload);
      if (current.length !== list.length) return false;
      return current.every(
        (row, index) =>
          valuesEqual(row.tipoCarga, list[index].tipoCarga) &&
          valuesEqual(row.tipo, list[index].tipo) &&
          valuesEqual(row.status, list[index].status)
      );
    },
    isStatusAtivo(status) {
      return sameValue(status, this.statusValues.ativo);
    },
    applyDefaultTipo() {
      const defaultTipo = this.defaultTipo;
      if (defaultTipo == null) return;
      let changed = false;
      this.rows = this.rows.map((row, index) => {
        if (row.tipo != null) return row;
        if (
          row.tipoCarga != null &&
          this.isPairUsed(index, row.tipoCarga, defaultTipo)
        ) {
          return row;
        }
        changed = true;
        return Object.assign({}, row, { tipo: defaultTipo });
      });
      if (changed) this.emitChange();
    },
    applyDefaultStatus() {
      const statusAtivo = this.statusValues.ativo;
      let changed = false;
      this.rows = this.rows.map((row) => {
        if (row.status != null) return row;
        changed = true;
        return Object.assign({}, row, { status: statusAtivo });
      });
      if (changed) this.emitChange();
    },
    // Mesmo "Tipo" pode repetir em tipos de carga diferentes; não no mesmo.
    isPairUsed(rowIndex, tipoCarga, tipo) {
      if (tipoCarga == null || tipo == null) return false;
      return this.rows.some(
        (row, index) =>
          index !== rowIndex &&
          sameValue(row.tipoCarga, tipoCarga) &&
          sameValue(row.tipo, tipo)
      );
    },
    isTipoDisabled(rowIndex, tipoValue) {
      const row = this.rows[rowIndex];
      if (!row || row.tipoCarga == null) return false;
      return this.isPairUsed(rowIndex, row.tipoCarga, tipoValue);
    },
    onTipoCargaChange(index, value) {
      const tipoCarga = value == null ? null : value;
      const row = this.rows[index];
      const patch = { tipoCarga };
      // Se o par (tipoCarga, tipo) já existe em outra linha, limpa o Tipo.
      if (
        tipoCarga != null &&
        row.tipo != null &&
        this.isPairUsed(index, tipoCarga, row.tipo)
      ) {
        patch.tipo = null;
      }
      this.patchRow(index, patch);
    },
    onTipoChange(index, value) {
      const tipo = value == null ? null : value;
      const row = this.rows[index];
      if (tipo != null && this.isPairUsed(index, row.tipoCarga, tipo)) return;
      this.patchRow(index, { tipo });
    },
    onStatusChange(index, checked) {
      this.patchRow(index, {
        status: checked ? this.statusValues.ativo : this.statusValues.inativo,
      });
    },
    patchRow(index, patch) {
      const next = this.rows.slice();
      next[index] = Object.assign({}, next[index], patch);
      this.rows = next;
      this.emitChange();
    },
    addRow() {
      if (!this.canAddRow) return;
      this.rows = this.rows.concat([
        createRow(
          { tipo: this.defaultTipo, status: this.statusValues.ativo },
          this.statusValues.ativo
        ),
      ]);
      this.emitChange();
    },
    removeRow(index) {
      if (this.rows.length <= 1) return;
      this.rows = this.rows.filter((_, i) => i !== index);
      this.emitChange();
    },
    emitChange() {
      this.$emit("update:value", this.rows.map(toPayload));
    },
  },
};
</script>

<style scoped lang="less">
@import "~@/shared/styles/variables.less";

.freight-table-carga {
  font-family: @font-primary;
  display: flex;
  flex-direction: column;
  gap: 12px;

  &__row {
    display: flex;
    align-items: flex-end;
    gap: 12px;
  }

  &__field {
    min-width: 0;

    &--tipo-carga {
      flex: 1 1 auto;
    }

    &--tipo {
      flex: 0 0 220px;
    }
  }

  &__label {
    display: block;
    margin-bottom: 8px;
    font-size: 14px;
    line-height: 22px;
    color: rgba(0, 0, 0, 0.88);
  }

  &__required {
    color: #ff4d4f;
  }

  &__actions {
    display: flex;
    align-items: center;
    gap: 8px;
    flex: 0 0 auto;
    padding-bottom: 4px;
  }

  &__add {
    width: 32px;
    height: 32px;
    padding: 0;
    border-radius: 6px;
    border-color: #d9d9d9;
    color: rgba(0, 0, 0, 0.65);

    /deep/ .anticon {
      font-size: 14px;
    }

    &:hover,
    &:focus {
      border-color: @color-primary;
      color: @color-primary;
    }
  }

  &__trash {
    flex: 0 0 auto;
    color: @color-danger-action !important;
    padding: 0 4px;

    &[disabled] {
      color: rgba(0, 0, 0, 0.25) !important;
    }
  }

  /deep/ .ant-select {
    width: 100%;
  }

  /deep/ .ant-select-selection {
    border-radius: 6px;
  }

  /deep/ .ant-switch-checked {
    background-color: @color-primary;
  }
}
</style>
